import { getApps, initializeApp } from 'firebase/app';
import { deleteToken, getMessaging, getToken, isSupported } from 'firebase/messaging';
import type { ShiftPushDeviceRequest } from 'shared-models';
import { createShiftJsonClient } from '../http/shift-json-client';
import { isAppleMobile, isShiftStandalone, registerShiftWorker } from './shift-pwa.utils';

export type ShiftPushResult = { status: 'registered' | 'unavailable' | 'denied'; message: string };
const request = createShiftJsonClient('לא ניתן לחבר את המכשיר להתראות.');
const configuration = () => {
  const config = { apiKey: process.env.REACT_APP_FIREBASE_API_KEY, authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID, messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID };
  return Object.values(config).every(Boolean) && process.env.REACT_APP_FIREBASE_VAPID_KEY ? config : null;
};

export class ShiftPushService {
  private token: string | null = null;
  private pending: Promise<ShiftPushResult> | null = null;

  async enable(): Promise<ShiftPushResult> {
    if (!window.isSecureContext || !('Notification' in window) || !('serviceWorker' in navigator)) {
      return { status: 'unavailable', message: 'התראות אינן נתמכות בדפדפן זה. נדרש חיבור HTTPS.' };
    }
    if (isAppleMobile() && !isShiftStandalone()) {
      return { status: 'unavailable', message: 'יש להוסיף את SHIFT למסך הבית ולפתוח אותה משם.' };
    }
    if (!configuration()) return { status: 'unavailable', message: 'שירות ההתראות עדיין אינו מוגדר בסביבה זו.' };
    const permission = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
    if (permission !== 'granted') return { status: 'denied', message: 'ההתראות חסומות. ניתן לאפשר אותן בהגדרות המכשיר.' };
    return this.sync();
  }

  async sync(): Promise<ShiftPushResult> {
    if (this.pending) return this.pending;
    this.pending = this.register();
    try { return await this.pending; } finally { this.pending = null; }
  }

  private async register(): Promise<ShiftPushResult> {
    const config = configuration();
    if (!config || !('Notification' in window) || Notification.permission !== 'granted' || !(await isSupported())) {
      return { status: 'unavailable', message: 'לא ניתן להפעיל התראות במכשיר זה כעת.' };
    }
    const registration = await registerShiftWorker();
    if (!registration) return { status: 'unavailable', message: 'לא ניתן להפעיל את האפליקציה במכשיר זה.' };
    if (!registration.active) {
      const worker = registration.installing ?? registration.waiting;
      if (!worker) throw new Error('לא ניתן להפעיל את שירות ההתראות.');
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => { worker.removeEventListener('statechange', changed); reject(new Error('הפעלת ההתראות נמשכת זמן רב. נסו שוב.')); }, 15_000);
        const changed = () => {
          if (worker.state !== 'activated' && worker.state !== 'redundant') return;
          clearTimeout(timeout);
          worker.removeEventListener('statechange', changed);
          if (worker.state === 'activated') resolve(); else reject(new Error('לא ניתן להפעיל את שירות ההתראות.'));
        };
        worker.addEventListener('statechange', changed);
        changed();
      });
    }
    const app = getApps().find(app => app.name === 'communicate-shift-web') ?? initializeApp(config, 'communicate-shift-web');
    const token = await getToken(getMessaging(app), { vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration });
    if (!token) throw new Error('לא ניתן לרשום את המכשיר להתראות.');
    this.token = token;
    const payload: ShiftPushDeviceRequest = { token };
    await request('/shift/push/devices', { method: 'POST', body: JSON.stringify(payload) });
    return { status: 'registered', message: 'ההתראות פעילות במכשיר זה.' };
  }

  async disable(): Promise<void> {
    await this.pending?.catch(() => undefined);
    try {
      if (this.token) await request('/shift/push/devices', { method: 'DELETE', body: JSON.stringify({ token: this.token }) }).catch(() => undefined);
    } finally {
      this.token = null;
      const app = getApps().find(app => app.name === 'communicate-shift-web');
      try {
        if (app) await deleteToken(getMessaging(app));
      } finally {
        if ('serviceWorker' in navigator) {
          const registration = await navigator.serviceWorker.getRegistration(`${process.env.PUBLIC_URL || ''}/`);
          const subscription = await registration?.pushManager?.getSubscription();
          if (subscription) await subscription.unsubscribe();
        }
      }
    }
  }
}

export const shiftPushService = new ShiftPushService();